<template>
  <div class="vdp-table">
    <template v-if="tableData?.length">
      <div class="vdp-table__header">
        <data-table-controller
          :selected-row-key="selectedRowKey"
          :table-data="tableData"
          :current-surface="currentSurface"
          @update:rows="handleUpdateRows"
          @update:selected-row-key="handleUpdateSelectedRowKey"
          @update:current-surface="handleUpdateCurrentSurface"
          @clear-sheets="handleClearSheets"
        />
      </div>

      <div class="vdp-table__body">
        <div
          class="vdp-table__table"
          v-for="(surface, surfaceIndex) in tableData"
          :key="`vdp-table_table_${surfaceIndex}`"
          v-show="currentSurface === surfaceIndex"
        >
          <data-table
            :columns="surface.columns"
            :rows="surface.rows"
            :surface-index="surfaceIndex"
            @update:selected-row-key="handleUpdateSelectedRowKey"
            @update:model="handleModelTransform"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="vdp-table__empty">
        <p class="vdp-table__empty-title">
          No data
        </p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch, type Ref } from 'vue';
import cloneDeep from 'lodash.clonedeep';

import pinia from '../../core/pinia';
import { useConfigStore, useEditorStore, useHistoryStore, useLineItemStore, useStepsStore, useVdpTableStore } from '../../stores';

import RenderPreviewService from '../../services/render-preview.service';

import DataTable from './DataTable.vue';
import DataTableController from './DataTableController.vue';

import type { IDataSet, IRow } from '../../types/table.types';
import type { IGetMockupResponse } from '../../types/response.type';
import { E_STEP_NAME } from '../../types/components.enums';

import RequestHelper from '../../utils/abort-controller';
import ExecutionDelay from 'execution-delay';
import emitter from '../../core/emitter';
import { E_EDITOR_CUSTOM_EVENT } from '../../types/custom-events.enums';

const configStore = useConfigStore(pinia);
const editorStore = useEditorStore(pinia);
const stepsStore = useStepsStore(pinia);
const lineItemStore = useLineItemStore(pinia);
const vdpTableStore = useVdpTableStore(pinia);
const historyStore = useHistoryStore(pinia);

const loadedDataset = computed(() => vdpTableStore.getDataset);
const currentStep = computed(() => stepsStore.getCurrentStep);
const beforeStep = computed(() => {
  const prevId = historyStore?.getLastItem?.prev;
  if (prevId) {
    return historyStore.fullHistory.get(prevId)?.data?.stepName;
  }
});
const backOfficeUrl = computed(() => configStore.getBackOfficeUrl);
const apiGatewayUrl = computed(() => configStore.getApiGatewayUrl);
const user = computed(() => configStore.getUser);
const tenantId = computed(() => configStore.getTenantId);
const pimProduct = computed(() => configStore.getPIM);
const product = computed(() => configStore.getProduct);
const editorStateId = computed(() => editorStore.getStateId);
const stateId = computed(() => editorStore.getAdaptiveStateId);
const datasetObservable = computed(() => editorStore.getDataset);

const tableData: Ref<IDataSet[]> = ref([]);
const currentSurface: Ref<number> = ref(0);
const selectedRowKey: Ref<string | null> = ref(null);
const mockups: Ref<IGetMockupResponse[]> = ref([]);

const previousTableData = ref(cloneDeep(tableData.value));

watch(tableData, (data) => {
  updateFirstLineItemsInEditor(data);
  editorStore.setDataSet(data);
}, { deep: true });
watch(currentStep, async (step) => {
  if (selectedRowKey.value) {
    const rowObject = getRowObjectFromTable(currentSurface.value, selectedRowKey.value);
    if (rowObject) {
      getPreview(rowObject);
    }
  }

  if (step === E_STEP_NAME.APPROVAL) {
    editorStore.setProofs(null);
  
    if (beforeStep.value === E_STEP_NAME.EDITOR) {
      emitter.on(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN_SUCCESS, async () => {
        emitter.off(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN_SUCCESS);
        await updateProofsInStore();
      });
      emitter.emit(E_EDITOR_CUSTOM_EVENT.SAVE_DESIGN);
    } else {
      await updateProofsInStore();
    }
  }
});
watch(datasetObservable, () => {
  ExecutionDelay.add('PUT_VDP', putVdpInState, 1000);
}, { deep: true });
watch(loadedDataset, setLoadedDataset, { deep: true, immediate: true });

function updateFirstLineItemsInEditor(data: IDataSet[]) {
  const beforeFirstLine = previousTableData.value[0]?.rows[0]?.row;
  const currentFirstLine = data[0]?.rows[0]?.row;
  if (
    JSON.stringify(beforeFirstLine) !== JSON.stringify(currentFirstLine) &&
    Boolean(currentFirstLine)
  ) {
    data[0].columns.forEach((col) => {
      emitter.emit(E_EDITOR_CUSTOM_EVENT.EDIT_ITEM_TEXT, {
        itemName: col,
        itemText: currentFirstLine[col],
      });
    });
  }
  previousTableData.value = cloneDeep(data);
}

function setLoadedDataset(datasets: IDataSet[] | null) {
  if (!datasets) return;
  tableData.value = JSON.parse(JSON.stringify(datasets));
  if (RequestHelper.abortController) {
    RequestHelper.abortController.abort();
  }
  RequestHelper.abortController = new AbortController();
  getPreview(tableData.value[0].rows[0]);
}

function handleClearSheets() {
  tableData.value = [{
    columns: [],
    rows: [],
  }];
}

function handleUpdateCurrentSurface(handleOptions: { surfaceIndex: number }) {
  currentSurface.value = handleOptions.surfaceIndex;
}

function handleUpdateRows(handleOptions: { surfaceIndex: number, rows: IRow[], columns: string[] }) {
  tableData.value[handleOptions.surfaceIndex] ??= {
    columns: handleOptions.columns,
    rows: [],
  }
  if (tableData.value[handleOptions.surfaceIndex].columns.length === 0) {
    tableData.value[handleOptions.surfaceIndex].columns = handleOptions.columns;
  }
  tableData.value[handleOptions.surfaceIndex].rows = handleOptions.rows;
}

function handleUpdateSelectedRowKey(handleOptions: { surfaceIndex: number, rowKey: string }) {
  if (selectedRowKey.value !== handleOptions.rowKey && !!handleOptions.rowKey) {
    const rowObject = tableData.value[handleOptions.surfaceIndex].rows.find((row) => row.key === handleOptions.rowKey);
    if (rowObject) {
      if (RequestHelper.abortController) {
        RequestHelper.abortController.abort();
      }
      RequestHelper.abortController = new AbortController();
      getPreview(rowObject);
    }
  }
  selectedRowKey.value = handleOptions.rowKey;
}

function getRowObjectFromTable(surfaceIndex: number, rowKey: string) {
  const surface = tableData.value[surfaceIndex];
  const rowObject = surface.rows.find((row) => row.key === rowKey);
  return rowObject;
}

function handleModelTransform(handleOptions: { surfaceIndex: number, model: Record<string, Record<number, any>> }) {
  const surfaceColumns = tableData.value[handleOptions.surfaceIndex].columns;
  const columnsKeys: Record<number, string> = surfaceColumns.reduce((acc: Record<number, string>, column, index) => {
    acc[index] = column;
    return acc;
  }, {});
  const mappedRows: IRow[] = [];
  Object.entries(handleOptions.model).forEach(([rowKey, modelRow]) => {
    let mappedRowIndex = mappedRows.findIndex((r) => r.key === rowKey);
    if (mappedRowIndex === -1) {
      mappedRows.push({
        key: rowKey,
        row: {},
      });
      mappedRowIndex = mappedRows.findIndex((r) => r.key === rowKey);
    }
    const row = mappedRows[mappedRowIndex].row;

    Object.keys(modelRow).forEach((key) => {
      row[columnsKeys[(key as unknown as number)]] = modelRow[(key as unknown as number)];
    });
    mappedRows[mappedRowIndex].row = row;
  });
  tableData.value[handleOptions.surfaceIndex].rows = mappedRows;
  const rowObject = tableData.value[handleOptions.surfaceIndex].rows.find((row) => row.key === selectedRowKey.value);
  if (RequestHelper.abortController) {
    RequestHelper.abortController.abort();
  }
  RequestHelper.abortController = new AbortController();
  if (rowObject) getPreview(rowObject);
}

async function getPreview(rowObject: IRow, isGetter?: boolean) {
  if (
    !backOfficeUrl.value ||
    !apiGatewayUrl.value ||
    !user.value ||
    !tenantId.value ||
    !pimProduct.value.productId ||
    !pimProduct.value.productVersionId ||
    !stateId.value ||
    !product.value.sku
  ) {
    console.error('Attempt to get VDP preview image failed. Missing required parameters');
    return; 
  }

  const options: {
    apigateway: string;
    backoffice: string;
    userId: string;
    token: string;
    tenantId: string;
    sku: string;
  } = {
    backoffice: backOfficeUrl.value,
    apigateway: apiGatewayUrl.value,
    userId: user.value.id,
    token: user.value.token,
    tenantId: tenantId.value,
    sku: product.value.sku,
  };
  const renderPreviewService = new RenderPreviewService(options);
  
  if (mockups.value.length === 0) {
    mockups.value = await renderPreviewService.getPreviewMockups(pimProduct.value.productId, pimProduct.value.productVersionId);
  }

  const mockupId = mockups.value[0]?.mockupId ?? null;
  const designId = stateId.value;
  const surfacesData = vdpTableStore.getSurfacesData;
  const previewUrl = await renderPreviewService.getPreviewVDP(rowObject, surfacesData, designId, mockupId);
  if (isGetter) return previewUrl;
  else editorStore.setPreview(previewUrl);
}

async function updateProofsInStore() {
  if (currentStep.value?.toLowerCase() !== E_STEP_NAME.APPROVAL) return;

  editorStore.setProofs(null);

  if (RequestHelper.abortController) {
    RequestHelper.abortController.abort();
  }
  RequestHelper.abortController = new AbortController();

  const requests: Promise<string>[] = tableData.value.map((surface) => {
    return surface.rows.map((rowObject) => {
      return getPreview(rowObject, true) as Promise<string>;
    });
  }).flat();
  
  editorStore.addProofChunk(null, requests.length);

  const chunkSize = 1;
  for (let i = 0; i < requests.length; i+=chunkSize) {
    const chunk = requests.slice(i, i + chunkSize);
    Promise.all(chunk).then((previews) => {
      editorStore.addProofChunk(previews, requests.length);
    });
  }

  Promise.all(requests).then((previews) => {
    editorStore.setProofs(previews);
  });
  
  editorStore.setDataSet(tableData.value);

  updateStateIdIfNotExist();
  updateLineItemCountRowsVdp();
  updateLineItemQuantity();
}

function updateStateIdIfNotExist() {
  const stateIsExist = editorStateId.value || !stateId.value;
  if (stateIsExist) return;
  lineItemStore.updateLineItem({
    stateId: [stateId.value],
  });
}

function updateLineItemCountRowsVdp() {
  const countRowsVdp = editorStore.getCountRowsVdp;
  if (!countRowsVdp) return;
  lineItemStore.updateLineItem({
    countRowsVdp,
  });
}

function updateLineItemQuantity() {
  const countRowsVdp = editorStore.getCountRowsVdp;
  if (!countRowsVdp) return;
  lineItemStore.updateLineItem({
    quantity: countRowsVdp,
  });
}

function putVdpInState() {
  return vdpTableStore.putVdpInState();
}

onMounted(async () => {
  if (!tableData.value.length) return;
  if (RequestHelper.abortController) {
    RequestHelper.abortController.abort();
  }
  RequestHelper.abortController = new AbortController();
  await getPreview(tableData.value[0].rows[0]);
})
</script>

<style scoped lang="scss">
.vdp-table {
  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    min-height: 300px;
  }

  &__empty-title {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 24px;
    font-weight: 400;
    line-height: 24px;
    color: $brand-color;
  }
}
</style>